import React from "react";

import Button from "@mui/material/Button";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";

import { t } from "@auto/services";
import { rest } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./__SignatureDetails.module.styl";

const getField = (row, camelName, snakeName) =>
  row?.[camelName] ?? row?.[snakeName] ?? "";

const formatDateTime = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.replace("T", " ").slice(0, 19);
  try { return new Date(value).toISOString().replace("T", " ").slice(0, 19); }
  catch { return String(value); }
};

const formatAdditionalValue = (value, displayType) => {
  if (value === null || value === undefined || value === "") return "";
  if (displayType === "datetime") return formatDateTime(value);
  if (displayType === "date" || displayType === "time") return String(value);
  if (displayType === "boolean") return value ? "Yes" : "No";
  if (displayType === "json") {
    try { return typeof value === "string" ? value : JSON.stringify(value); }
    catch { return String(value); }
  }
  return String(value);
};

const fullName = (row) => [
  getField(row, "firstName", "first_name"),
  getField(row, "lastName", "last_name")
].map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const status = (row) => String(getField(row, "requestStatus", "request_status") || "").toUpperCase();

const decisionTime = (row) =>
  formatDateTime(getField(row, "approvedAt", "approved_at")) ||
  formatDateTime(getField(row, "rejectedAt", "rejected_at")) ||
  formatDateTime(getField(row, "reviewedAt", "reviewed_at"));

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <section className={styles.card}>
    <header className={styles.cardHeader}>
      <div className={styles.cardTitleRow}>
        <div className={styles.cardIconWrap}><Icon /></div>
        <div>
          <h2 className={styles.cardTitle}>{t(title)}</h2>
          {subtitle ? <div className={styles.cardSubtitle}>{t(subtitle)}</div> : null}
        </div>
      </div>
    </header>
    <div className={styles.cardBody}>{children}</div>
  </section>
);

const Field = ({ label, value, children }) => {
  if ((value === null || value === undefined || value === "") && !children) return null;
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{t(label)}</div>
      <div className={styles.fieldValue}>{children || String(value)}</div>
    </div>
  );
};

const LinkedField = ({ gui, label, value, href }) => {
  if (!value || !href) return null;
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{t(label)}</div>
      <button type="button" className={styles.linkValue} onClick={() => gui.goTo(href)}>{String(value)}</button>
    </div>
  );
};

export const readOperatorAccountRequest = async (gid) => {
  const rows = await rest.request(getServiceUri() + "operator-account-request/search", "POST", {
    columns: [], query: `gid==${Number(gid)}`, limit: 2, offset: 0
  });
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (rows.length !== 1) throw new Error("Expected exactly one operator_account_request for gid " + gid);
  return rows[0];
};

export const OperatorAccountRequestDetailsWidget = ({
  gui,
  id,
  canManage = false,
  approveRequest,
  rejectRequest
}) => {
  const [request, setRequest] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const value = await readOperatorAccountRequest(id);
      setRequest(value);
    } catch (err) {
      setError(err?.message || String(err));
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => { load(); }, [load]);

  const runDecision = async (callback) => {
    if (!callback || !request?.gid) return;
    setSaving(true);
    setError("");
    try {
      await callback(request, gui);
      await load();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !request) return <div className={styles.loading}>Loading</div>;
  if (error && !request) return <div className={styles.error}>{error}</div>;
  if (!request) return null;

  const requestStatus = status(request);
  const canReview = requestStatus === "READY" || requestStatus === "ERROR";
  const createdUserGid = getField(request, "createdOperatorUserGid", "created_operator_user_gid");
  const reviewerUserId = getField(request, "reviewerBackofficeUserId", "reviewer_backoffice_user_id");

  return (
    <div className={styles.page}>
      <section className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.heroIconWrap}><ManageAccountsOutlinedIcon className={styles.heroIcon} /></div>
          <div>
            <h1 className={styles.headerTitle}>{fullName(request) || "Operator enrollment request"}</h1>
            <div className={styles.headerSubtitle}>{t("Request")} <span>#</span>{request.gid || id}</div>
          </div>
        </div>
        <div className={styles.headerAside}>
          <div className={styles.statusPill}>{t(requestStatus) || t("PENDING")}</div>
          {canManage && canReview && (approveRequest || rejectRequest) ? (
            <div className={styles.headerActions}>
              {approveRequest ? (
                <Button variant="contained" color="success" disabled={saving} onClick={() => runDecision(approveRequest)}>
                  {t("Approve")}
                </Button>
              ) : null}
              {rejectRequest ? (
                <Button variant="contained" color="error" disabled={saving} onClick={() => runDecision(rejectRequest)}>
                  {t("Reject")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.gridTwo}>
        <SectionCard icon={PersonOutlineOutlinedIcon} title="voter.applicant" subtitle="common.requestedSignatureAccountIdentity">
          <div className={styles.fieldsGrid}>
            <Field label="person.firstName" value={getField(request, "firstName", "first_name")} />
            <Field label="person.lastName" value={getField(request, "lastName", "last_name")} />
            <Field label="user.username" value={request.username} />
            <Field label="person.nationalId" value={getField(request, "nationalId", "national_id")} />
            <Field label="common.deviceId" value={getField(request, "deviceId", "device_id")} />
          </div>
        </SectionCard>

        <SectionCard icon={ScheduleOutlinedIcon} title="Enrollment" subtitle="common.requestLifecycle">
          <div className={styles.fieldsGrid}>
            <Field label="common.status" value={requestStatus} />
            <Field label="voter.submittedAt" value={formatDateTime(getField(request, "submittedAt", "submitted_at"))} />
            <Field label="voter.reviewedAt" value={formatDateTime(getField(request, "reviewedAt", "reviewed_at"))} />
            <Field label="user.decisionTime" value={decisionTime(request)} />
          </div>
        </SectionCard>
      </div>

      <div className={styles.gridTwo}>
        <SectionCard icon={BadgeOutlinedIcon} title="common.createdUser" subtitle="common.signatureUserCreatedAfterApproval">
          <div className={styles.fieldsGrid}>
            <LinkedField
              gui={gui}
              label="common.createdUser"
              value={createdUserGid}
              href={createdUserGid ? `/operator-user/${createdUserGid}` : ""}
            />
          </div>
        </SectionCard>

        <SectionCard icon={SupervisorAccountOutlinedIcon} title="common.decision" subtitle="common.reviewByBackoffice">
          <div className={styles.fieldsGrid}>
            <Field label="voter.reviewerUserGid" value={reviewerUserId} />
            <Field label="common.decisionReason" value={getField(request, "decisionReason", "decision_reason")} />
          </div>
        </SectionCard>
      </div>

      <SectionCard icon={VpnKeyOutlinedIcon} title="common.signature" subtitle="common.requestedPublicSigningIdentity">
        <div className={styles.fieldsGrid}>
          <Field label="common.publicKey" value={getField(request, "publicKey", "public_key")} />
        </div>
      </SectionCard>

      <SectionCard icon={BadgeOutlinedIcon} title="common.additionalInformation" subtitle="common.enrollmentSpecificAttributes">
        <div className={styles.fieldsGrid}>
          <Field
            label="common.communeGid"
            value={formatAdditionalValue(
              getField(request, "communeGid", "commune_gid"),
              "text"
            )}
          />
          <Field
            label="common.departmentGid"
            value={formatAdditionalValue(
              getField(request, "departmentGid", "department_gid"),
              "text"
            )}
          />
          <Field
            label="common.operatorRole"
            value={formatAdditionalValue(
              getField(request, "operatorRole", "operator_role"),
              "text"
            )}
          />
          <Field
            label="common.votingCenterGid"
            value={formatAdditionalValue(
              getField(request, "votingCenterGid", "voting_center_gid"),
              "text"
            )}
          />
        </div>
      </SectionCard>
    </div>
  );
};

export default OperatorAccountRequestDetailsWidget;
